import React, {useState} from 'react'
import { graphql } from 'gatsby'
import { Container, Tabs, Tab } from 'react-bootstrap'
import Layout from '../components/Layout'
import HeroRight from '../components/HeroRight'
import OverviewText from '../components/OverviewText'
import ProAccordion from '../components/Accordion'
import TabWrapper from '../components/TabWrapper'
import IconLink from '../components/Common/StyledButton/IconLink'
import ProcopioForm from "../components/ProcopioForm";
import {useResourcesByService} from "../hooks/use-resources-by-service";
import {useEventsByService} from "../hooks/use-events-by-service";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import StyledCard from "../components/Common/StyledCard";
import EventCards from "../list/resources/Events/EventCards";
import PeopleGridView from "../components/PeopleList/PeopleGridView";
import Seo from 'gatsby-plugin-wpgraphql-seo';
import {useTestimoinalsByService} from "../hooks/use-testimonials-by-service";
import TestimonialGrid from "../components/FlexibleContent/TestimonialGrid";
import FlexibleContent from "../components/FlexibleContent";

const ServiceTemplate = ({ data: { post }, pageContext: { posts } }) => {
	const accordionData = post?.serviceInformation?.serviceAccordionPanel ?? []
	const overviewHeading = post?.serviceInformation?.serviceOverviewHeading
	const overviewContent = post?.serviceInformation?.serviceOverviewContent
	const resources = useResourcesByService(post.slug)
	const events = useEventsByService(post.slug)
	const testimonials = useTestimoinalsByService(post.slug)

	const seo = post?.seo
	if (seo?.metaDesc !== '') {
		const metaDescription =  overviewContent?.replace(/<[^>]+>/g, '').trim();
		seo.metaDesc = metaDescription
		seo.opengraphDescription = metaDescription
	}

	const [activeTab, setActiveTab] = useState('information');

	const handleContactClick = (e) => {
		setActiveTab('contact')
	}

	const handleActiveTab = (key) => {
		setActiveTab(key)
	}

	return (
		<Layout title={post.title}>
			<Seo post={{seo: seo}} />
			<Container className="pt-4" fluid="xl">
				<IconLink
					align="right"
					cta="Back to Services & Sectors"
					href="/services-and-sectors/"
					size="small"
					iconColor="orange"
					name="left"
				/>
			</Container>

			<HeroRight
				heading={post.title}
				content={post.content}
				langSelect={post.translations}
				langLocale={post.locale.locale}
				image={
					post?.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
				}
				ctaLink={'#sector-tabs-tab-contact'}
				ctaLabel={"Let's Talk"}
				ctaClick={handleContactClick}
			/>

			<TabWrapper excludePaddingBottom={!!post.flexibleContent?.flexibleContent?.length}>
				<Tabs defaultActiveKey="information" id="sector-tabs" activeKey={activeTab} onSelect={handleActiveTab} className="nav-tabs--alt">
					<Tab eventKey="information" title="Information">
						<>
              {overviewHeading && overviewContent && (
								<OverviewText title={overviewHeading} text={overviewContent} />
							)}
							{accordionData?.map(({ heading, content }, index) => (
										<ProAccordion
											title={heading}
											content={content}
											index={index + 1}
											key={index}
											first={index === 0}
										/>
								  ))}
						</>
					</Tab>
					<Tab eventKey="people" title="People">
						<>
							<PeopleGridView name="PeopleGridView" service={post.slug} leaders={post?.serviceInformation?.serviceLeader} />
						</>
					</Tab>
					{!!resources?.length && (
						<Tab eventKey="resources" title="Resources">
							<Row>
								{resources.map(({ resource }, index) => (
									<Col xs={6} md={4} key={`people-resource-${index}`}>
										<StyledCard
											variation="CardD"
											service={resource?.resourceInformation?.service}
											label={resource.resourceTypes?.nodes[0]?.name}
											labelId={resource.resourceTypes?.nodes[0]?.slug}
											href={resource.uri}
											eventDetails={null}
											image={
												resource.featuredImage?.node?.localFile?.childImageSharp
													?.gatsbyImageData
											}
											imageSize="medium"
											title={resource.title}
										/>
									</Col>
								))}
							</Row>
						</Tab>
					)}
					{!!events?.length &&
					<Tab eventKey="Events" title="Events">
						<Row>
							<EventCards events={events} title={null} />
						</Row>
					</Tab>
					}
					{!!testimonials?.length && (
						<Tab eventKey="testimonials" title="Testimonials">
							<TestimonialGrid testimonial={testimonials} />
						</Tab>
					)}
					<Tab eventKey="contact" title="Contact">
						<ProcopioForm presetValues={{
							'Firm Notes': `Service/Sector Contacted: ${post.title}`
						}} />
					</Tab>
				</Tabs>
			</TabWrapper>

			<FlexibleContent post={post} />

		</Layout>
	)
}

export default ServiceTemplate

export const pageQuery = graphql`
	query ServiceById(
		# these variables are passed in via createPage.pageContext in gatsby-node.js
		$id: String!
	) {
		# selecting the current post by id
		post: wpService(id: { eq: $id }) {
			id
			excerpt
			content
			title
			slug
			date(formatString: "MMMM DD, YYYY")
			seo {
				...SeoFields
			}
			serviceInformation {
				serviceLeader {
					... on WpPeople {
						id
						slug
						title
						uri
					}
				}
				serviceOverviewHeading
				serviceOverviewContent
				serviceAccordionPanel {
					heading
					content
				}
			}
			locale {
				id
				locale
			}
			translations {
				id
				locale
				href
			}
			featuredImage {
				node {
					altText
					localFile {
						childImageSharp {
							gatsbyImageData(
								layout: CONSTRAINED,
								aspectRatio: 1.6,
								width: 1000
							)
						}
					}
				}
			}
			flexibleContent {
				... on WpService_Flexiblecontent {
					flexibleContent {
						__typename
						... on WpService_Flexiblecontent_FlexibleContent_HeroWithVideo {
							fieldGroupName
							headline
							content
							image {
								altText
								localFile {
									childImageSharp {
										gatsbyImageData(
											layout: CONSTRAINED
											aspectRatio: 1.59
											width: 1000
										)
									}
								}
							}
							videoLink
							ctaText
							ctaLink {
								__typename
								... on WpService {
									uri
								}
							}
						}

						... on WpService_Flexiblecontent_FlexibleContent_HeroHeadline {
							fieldGroupName
							headline
							includeContainer
							includePadding
							menu
						}

						... on WpService_Flexiblecontent_FlexibleContent_ResourcesHeadingSearch {
							fieldGroupName
							headline
							includeContainer
							includePadding
						}

						... on WpService_Flexiblecontent_FlexibleContent_ContactForm {
							fieldGroupName
							gravityForm
						}

						... on WpService_Flexiblecontent_FlexibleContent_LeadershipTeaser {
							fieldGroupName
							headline
							content
							people {
								...peopleData
							}
							ctaText
							ctaLink {
								__typename
								... on WpService {
									uri
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_AlternatingContent {
							fieldGroupName
							backgroundStyle
							layout
							headline
							content
							accoladesTeaser
							accolades {
								... on WpPeople {
									id
									title
									date
								}
							}
							image {
								altText
								localFile {
									childImageSharp {
										gatsbyImageData(
											layout: CONSTRAINED
											aspectRatio: 1.4
											width: 650
										)
									}
								}
							}
							videoLink
							ctaText
							ctaLink {
								__typename
								... on WpService {
									uri
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_OfficeList {
							fieldGroupName
							offices {
								... on WpOffice {
									id
									title
									officeInformation {
										proOfficeZipcode
										proOfficeStreet
										proOfficeState
										proOfficePhone
										proOfficePeopleLink
										proOfficeLng
										proOfficeLat
										proOfficeFax
										proOfficeImage {
											id
											localFile {
												childImageSharp {
													gatsbyImageData(
														layout: CONSTRAINED
														aspectRatio: 1
														width: 150
													)
												}
											}
										}
										proOfficeDirectionsLink
										proOfficeCity
										fieldGroupName
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_TestimonialGrid {
							fieldGroupName
							headline
							includeSearch
							disclaimerContent
							disclaimerHeadline
							testimonial {
								... on WpTestimonial {
									id
									content
									testimonialInformation {
										author
										jobtitle
										size
										testimonialfull
										variant
										proService {
											... on WpService {
												slug
												title
											}
										}
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_AccoladeGrid {
							fieldGroupName
							headline
							limit
							accoladeGroup {
								... on WpPost {
									id
									uri
									accoladeInformation {
										date
										accolade
										fieldGroupName
										accoladetitle
										source
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_HeroSlider {
							fieldGroupName
							content
							headline
							search
							slides {
								fieldGroupName
								title
								label
								slideLink {
									... on WpPost {
										id
										uri
									}
									... on WpService {
										id
										uri
									}
									... on WpService {
										id
										uri
										serviceTypes {
											nodes {
												slug
											}
										}
									}
								}
								slideImage {
									id
									altText
									localFile {
										childImageSharp {
											gatsbyImageData(layout: CONSTRAINED, quality: 90)
										}
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_TestimonialsSlider {
							fieldGroupName
							headline
							ctaText
							ctaLink {
								__typename
								... on WpService {
									uri
								}
							}
							testimonialGroup {
								... on WpTestimonial {
									id
									content
									testimonialInformation {
										author
										jobtitle
										fieldGroupName
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_CtaCallout {
							fieldGroupName
							headline
							ctaText
							ctaLink {
								__typename
								... on WpService {
									uri
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_ManagementTabs {
							fieldGroupName
							teamLeaders {
								team {
									teamLabel
									teamLeader {
										...peopleData
									}
								}
							}
							nonLegalManagement {
								nonLegalMember {
									...peopleData
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_PeopleGrid {
							fieldGroupName
							showContactInformation
							sectionTitle
							people {
								...peopleData
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_FeaturedResources {
							fieldGroupName
							includeRecent
							featuredResources {
								...ResourcePost
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_RecommendedVideos {
							fieldGroupName
							heading
							videos {
								...ResourcePost
							}
							ctaButton {
								text
								href
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_LegalDisclaimer {
							disclaimer
							fieldGroupName
							headline
						}
						... on WpService_Flexiblecontent_FlexibleContent_UpcomingEvents {
							fieldGroupName
							heading
							resources {
								...ResourcePost
							}
							ctaButton {
								text
								href
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_Submenu {
							fieldGroupName
							menu
						}
						... on WpService_Flexiblecontent_FlexibleContent_QuickSearch {
							fieldGroupName
							headingText
							introCopy
							links {
								text
								href
							}
							ctaButton {
								text
								href
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_MediaContacts {
							newsletterCta {
								ctaText
								ctaLink {
									url
								}
								newsletterHeading
							}
							fieldGroupName
							contactPerson {
								accessibleContactPhone
								contactEmail
								contactFax
								contactNameTitle
								contactPhone
								contactType
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_BrowseSectors {
							fieldGroupName
							headingText
							type
						}
						... on WpService_Flexiblecontent_FlexibleContent_ResourcesCarousel {
							fieldGroupName
							headingText
							description
							resources {
								... on WpPost {
									title
									uri
									id
									date(formatString: "MMMM DD, YYYY")
									resourceTypes {
										nodes {
											slug
											name
										}
									}
									featuredImage {
										node {
											altText
											localFile {
												childImageSharp {
													gatsbyImageData(
														aspectRatio: 0.75
														layout: CONSTRAINED
														width: 485
													)
												}
											}
										}
									}
									resourceInformation {
										service {
											... on WpService {
												title
												uri
												serviceTypes {
													nodes {
														slug
														name
													}
												}
											}
										}
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_HomepageHeroSlider {
							fieldGroupName
							slide {
								published
								title
								label {
									tag
									label
								}
								description
								descriptionLabel
								image {
									altText
									localFile {
										childImageSharp {
											gatsbyImageData(
												layout: FULL_WIDTH
												aspectRatio: 1.4
												width: 2500
											)
										}
									}
								}
								ctaButton1 {
									text
									href {
										... on WpService {
											uri
										}
										... on WpService {
											id
											uri
											serviceTypes {
												nodes {
													slug
												}
											}
										}
									}
								}
								ctaButton2 {
									text
									href {
										... on WpService {
											uri
										}
										... on WpService {
											id
											uri
											serviceTypes {
												nodes {
													slug
												}
											}
										}
									}
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_Accordion {
							fieldGroupName
							columnWidth
							accordion {
								accordionContent
								accordionTitle
								optionalCta {
									ctaLink
									ctaText
								}
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_GeneralContent {
							fieldGroupName
							columnWidth
							content
						}
						... on WpService_Flexiblecontent_FlexibleContent_ResourcesByCategory {
							fieldGroupName
							category {
								slug
							}
						}
						... on WpService_Flexiblecontent_FlexibleContent_VideosGrid {
							fieldGroupName
							excludePaddingBottom
							excludePaddingTop
							video {
								image {
									altText
									localFile {
										childImageSharp {
											gatsbyImageData(
												layout: CONSTRAINED,
												aspectRatio: 1.77,
												width: 1076
											)
										}
									}
								}
								vimeoPlayer
								title
								description
							}
						}
					}
				}
			}
		}
	}
`
